import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeftIcon, ArrowRightIcon, PauseIcon, PlayIcon } from "lucide-react";
import { heroSlides } from "../../data/heroSlides";
import { products } from "../../data/products";
import { formatPrice } from "../../utils/currency";
const DURATION = 6500;
const ease = [0.23, 1, 0.32, 1] as const;
const container = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.06,
      delayChildren: 0.05
    }
  },
  exit: {
    transition: {
      staggerChildren: 0.02
    }
  }
};
const item = {
  hidden: {
    opacity: 0,
    y: 24
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ease
    }
  },
  exit: {
    opacity: 0,
    y: -12,
    transition: {
      duration: 0.15,
      ease
    }
  }
};
export function HeroSlider() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const slide = heroSlides[index];
  const product = products.find(p => p.slug === slide.productSlug);
  const go = useCallback((dir: number) => {
    setIndex(i => (i + dir + heroSlides.length) % heroSlides.length);
  }, []);
  useEffect(() => {
    if (paused) return;
    const t = setTimeout(() => go(1), DURATION);
    return () => clearTimeout(t);
  }, [index, paused, go]);
  return <section className="relative isolate h-[100svh] min-h-[680px] max-h-[980px] overflow-hidden bg-night" aria-roledescription="carousel" aria-label="Featured" data-id="element-193" data-mp="1jkjvnnxrvmj1">
      <AnimatePresence initial={false} data-id="element-194" data-mp="2c7w4ywex9xue">
        <motion.div key={slide.id} className="absolute inset-0 -z-10" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease
      }} data-id="element-195" data-mp="2c8xqxvxh84qw">
          <motion.img src={slide.image} alt="" className={`h-full w-full object-cover ${slide.imagePosition}`} initial={{
          scale: 1.12
        }} animate={{
          scale: 1
        }} transition={{
          duration: DURATION / 1000 + 1,
          ease: "linear"
        }} data-id="element-196" data-mp="2zxqhtdz8rmen" />
        </motion.div>
      </AnimatePresence>
      <div className="absolute inset-0 -z-10 bg-night/55 md:bg-night/35" aria-hidden="true" data-id="element-197" data-mp="xatlmdkgh1y5" />
      <div className="absolute inset-x-0 bottom-0 -z-10 h-48 bg-night/50" aria-hidden="true" data-id="element-198" data-mp="jjf0a032sonx" />

      <div className="mx-auto flex h-full max-w-7xl flex-col px-6 pb-8 pt-28 lg:pt-[104px]" data-id="element-199" data-mp="ohq6m0u5wh1e">
        <div className="flex flex-1 items-center" data-id="element-200" data-mp="9kz7ztsbb9cu">
          <AnimatePresence mode="wait" data-id="element-201" data-mp="ektz9yg020yf">
            <motion.div key={slide.id} variants={container} initial="hidden" animate="show" exit="exit" className="max-w-2xl" aria-live="polite" data-id="element-202" data-mp="elwrc9p269fx">
              <motion.p variants={item} className="font-display text-sm font-semibold text-brand" data-id="element-203" data-mp="1uz3lalwrrdm3">
                {String(index + 1).padStart(2, "0")} — {slide.label}
              </motion.p>
              <h1 className="mt-4 font-display text-5xl font-extrabold leading-[0.95] tracking-tight sm:text-7xl lg:text-[96px]" data-id="element-204" data-mp="2a24qgjnyptzk">
                <motion.span variants={item} className="block" data-id="element-205" data-mp="253theb5hrnie">
                  {slide.title}
                </motion.span>
                <motion.span variants={item} className="block text-brand" data-id="element-206" data-mp="2ivzx5w4qfshb">
                  {slide.accent}
                </motion.span>
              </h1>
              <motion.p variants={item} className="mt-7 max-w-md text-lg leading-relaxed text-white/80" data-id="element-207" data-mp="2sscqgswd84bj">
                {slide.text}
              </motion.p>
              <motion.div variants={item} className="mt-10 flex flex-wrap gap-3" data-id="element-208" data-mp="2nt9x45pqzmje">
                <Link to={slide.primary.to} className="group inline-flex items-center gap-2 rounded-full bg-brand px-7 py-4 text-sm font-bold text-night transition-colors hover:bg-white" data-id="element-209" data-mp="31src5td8rlfk">
                  {slide.primary.label}
                  <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-brand group-hover:translate-x-1" aria-hidden="true" data-id="element-210" data-mp="13eq01r6bwta0" />
                </Link>
                <Link to={slide.secondary.to} className="inline-flex items-center rounded-full border border-white/30 px-7 py-4 text-sm font-semibold backdrop-blur-sm transition-colors hover:border-white hover:bg-white/10" data-id="element-211" data-mp="19s3p655fbssz">
                  {slide.secondary.label}
                </Link>
              </motion.div>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="flex items-end justify-between gap-6" data-id="element-212" data-mp="v50u054kpw8t">
          <div className="flex flex-1 items-center gap-4" data-id="element-213" data-mp="v3poo0vhcgfl">
            <div className="grid flex-1 grid-cols-4 gap-3 sm:max-w-xl" role="tablist" aria-label="Slides" data-id="element-214" data-mp="zw0ol3q80285">
              {heroSlides.map((s, i) => <button key={s.id} type="button" role="tab" aria-selected={i === index} aria-label={`Slide ${i + 1}: ${s.label}`} onClick={() => setIndex(i)} className="group text-left" data-id="element-215" data-mp="2eac36o62fhk" data-mpi={i}>
                  <span className="block h-[3px] overflow-hidden rounded-full bg-white/20" data-id="element-216" data-mp="3m2vg4p37h8gc">
                    {i < index && <span className="block h-full w-full bg-white" data-id="element-217" data-mp="3i236vvuc33k2" />}
                    {i === index && <motion.span key={`${index}-${paused}`} className="block h-full bg-brand" initial={{
                  width: paused ? "100%" : "0%"
                }} animate={{
                  width: "100%"
                }} transition={{
                  duration: paused ? 0 : DURATION / 1000,
                  ease: "linear"
                }} data-id="element-218" data-mp="3d2qih6ntdofd" />}
                  </span>
                  <span className={`mt-2.5 hidden whitespace-nowrap text-xs font-semibold transition-colors sm:block ${i === index ? "text-white" : "text-white/50 group-hover:text-white/80"}`} data-id="element-219" data-mp="2ubfwn3wy41uc">
                    {s.label}
                  </span>
                </button>)}
            </div>
            <button type="button" onClick={() => setPaused(p => !p)} className="rounded-full border border-white/20 p-2.5 transition-colors hover:border-white" aria-label={paused ? "Play slideshow" : "Pause slideshow"} data-id="element-220" data-mp="25se3u5b29ctq">
              {paused ? <PlayIcon className="h-4 w-4" data-id="element-221" data-mp="33djoel8df8c8" /> : <PauseIcon className="h-4 w-4" data-id="element-222" data-mp="33gnyfz8ioyat" />}
            </button>
          </div>

          <div className="hidden items-center gap-3 lg:flex" data-id="element-223" data-mp="2opoux2mjgzy3">
            <AnimatePresence mode="wait" data-id="element-224" data-mp="2to02f6pd2yd5">
              {product && <motion.div key={product.slug} initial={{
              opacity: 0,
              y: 12
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -8
            }} transition={{
              duration: 0.25,
              ease
            }} data-id="element-225" data-mp="3hew0umobortd">
                  <Link to={`/product/${product.slug}`} className="group flex w-80 items-center gap-4 rounded-2xl border border-white/10 bg-night/60 p-3 pr-5 backdrop-blur-xl transition-colors hover:border-brand/60" data-id="element-226" data-mp="15raexuas6ik5">
                    <img src={product.images[0]} alt="" className="h-16 w-16 shrink-0 rounded-xl bg-white object-contain" data-id="element-227" data-mp="1fj6pi4u20159" />
                    <div className="min-w-0 flex-1" data-id="element-228" data-mp="murb9kiqxmuz">
                      <p className="truncate font-display font-semibold" data-id="element-229" data-mp="10t8sr0i2634g">{product.name}</p>
                      <p className="text-sm text-brand" data-id="element-230" data-mp="vstgkc9ij1o2">{formatPrice(product.price)}</p>
                    </div>
                    <ArrowRightIcon className="h-5 w-5 text-white/50 transition-[color,transform] duration-200 group-hover:translate-x-1 group-hover:text-brand" aria-hidden="true" data-id="element-231" data-mp="2c61k4a6xspjz" />
                  </Link>
                </motion.div>}
            </AnimatePresence>
            <div className="flex gap-2" data-id="element-232" data-mp="2w732ni8yr5og">
              <button type="button" onClick={() => go(-1)} className="flex h-12 w-12 items-center justify-center rounded-full border border-white/20 transition-colors hover:border-white hover:bg-white hover:text-night" aria-label="Previous slide" data-id="element-233" data-mp="30astyfh0w1d6">
                <ArrowLeftIcon className="h-5 w-5" data-id="element-234" data-mp="3eydwa762pwk1" />
              </button>
              <button type="button" onClick={() => go(1)} className="flex h-12 w-12 items-center justify-center rounded-full border border-white/20 transition-colors hover:border-white hover:bg-white hover:text-night" aria-label="Next slide" data-id="element-235" data-mp="3k0383p14ee96">
                <ArrowRightIcon className="h-5 w-5" data-id="element-236" data-mp="1tn47bhnr3x9h" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}